import React, { useEffect, useMemo, useState } from "react";
import InstructorLayout from "../../layouts/InstructorLayout";
import {
  EmergencyContact,
  FilledDropDown,
  GraduationCap,
  LockIcon,
  OrangeProfile,
  OrangeProfileFilled,
  ProfileLocationIcon,
  Upload,
} from "../../components/Icons";
import { Controller, useForm } from "react-hook-form";
import { getData } from "country-list";
import FormInput from "../../components/common/FormInput";
import PhoneInput from "react-phone-input-2";
import { useGetPublicCoursesQuery } from "../../services/auth/authApi";
import { Plus, X } from "lucide-react";
import Button from "../../components/common/Button";
import { useNavigate } from "react-router-dom";
import {
  useGetInstructorProfileQuery,
  useUpdateInstructorAcademicDetailsMutation,
  useUpdateInstructorInformationMutation,
} from "../../services/instructor/instructor";
import Toast from "../../components/common/Toast";
import InstructorIdBadge from "../../components/instructor/InstructorIdBadge";
import { Edit } from "../../components/instructor/icons";

const sectionClassName =
  "mt-6 rounded-[23px] border border-[#D9D9D9] bg-[#F9F6F6] px-4 pb-6 pt-6 sm:px-6 sm:pb-8 sm:pt-8 lg:px-10";
const sectionTitleClassName =
  "flex items-center gap-2 text-lg font-medium leading-6 text-black sm:gap-2.5 sm:text-xl sm:leading-[26px] lg:text-[22px]";
const labelClassName = "block text-base text-black sm:text-lg";
const inputClassName =
  "px-5 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363] placeholder:font-light placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none";
const gridThreeClassName =
  "grid grid-cols-1 gap-5 sm:gap-7 md:grid-cols-2 lg:grid-cols-3";

const INFO_FIELD_KEYS = [
  "firstName",
  "lastName",
  "fatherName",
  "phoneNumber",
  "email",
  "dateOfBirth",
  "bio",
  "address",
  "city",
  "country",
  "postalCode",
  "emergencyFullName",
  "emergencyRelationship",
  "emergencyPhoneNo",
];

const ACADEMIC_FIELD_KEYS = [
  "qualification",
  "degreeTitle",
  "graduationYear",
  "totalMarks",
  "obtainedMarks",
  "institution",
];

const normalizeValue = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

const buildProfileSnapshot = (source, courses = []) => ({
  firstName: normalizeValue(source.firstName),
  lastName: normalizeValue(source.lastName),
  fatherName: normalizeValue(source.fatherName),
  phoneNumber: normalizeValue(source.phoneNumber),
  email: normalizeValue(source.email),
  dateOfBirth: normalizeValue(source.dateOfBirth),
  bio: normalizeValue(source.bio),
  address: normalizeValue(source.address),
  city: normalizeValue(source.city),
  country: normalizeValue(source.country),
  postalCode: normalizeValue(source.postalCode),
  emergencyFullName: normalizeValue(source.emergencyFullName),
  emergencyRelationship: normalizeValue(source.emergencyRelationship),
  emergencyPhoneNo: normalizeValue(source.emergencyPhoneNo),
  qualification: normalizeValue(source.qualification),
  degreeTitle: normalizeValue(source.degreeTitle),
  graduationYear: normalizeValue(source.graduationYear),
  totalMarks: normalizeValue(source.totalMarks),
  obtainedMarks: normalizeValue(source.obtainedMarks),
  institution: normalizeValue(source.institution),
  courses: [...courses].sort(),
});

const snapshotFromInstructorProfile = (profile) =>
  buildProfileSnapshot(
    {
      firstName: profile?.name?.split(" ")[0] || "",
      lastName: profile?.name?.split(" ").slice(1).join(" ") || "",
      fatherName: profile?.fatherName,
      phoneNumber: profile?.phoneNumber,
      email: profile?.email,
      dateOfBirth: profile?.dateOfBirth
        ? profile.dateOfBirth.split("T")[0]
        : "",
      bio: profile?.bio,
      address: profile?.residenceInfo?.address,
      city: profile?.residenceInfo?.city,
      country: profile?.residenceInfo?.country,
      postalCode: profile?.residenceInfo?.postalCode,
      emergencyFullName: profile?.emergencyInfo?.fullName,
      emergencyRelationship: profile?.emergencyInfo?.relationship,
      emergencyPhoneNo: profile?.emergencyInfo?.phoneNumber,
      qualification: profile?.academicInfo?.qualification,
      degreeTitle: profile?.academicInfo?.degreeTitle,
      graduationYear: profile?.academicInfo?.graduationYear,
      totalMarks: profile?.academicInfo?.totalMarks,
      obtainedMarks: profile?.academicInfo?.obtainedMarks,
      institution: profile?.academicInfo?.institution,
    },
    profile?.academicInfo?.coursePreferences || [],
  );

const snapshotsEqual = (left, right) =>
  JSON.stringify(left) === JSON.stringify(right);

const EditProfile = () => {
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
  const countries = getData();
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImageError, setProfileImageError] = useState("");
  const [currentCourse, setCurrentCourse] = useState("");
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [initialSnapshot, setInitialSnapshot] = useState(null);

  const {
    data: publicCoursesResponse,
    isLoading: isPublicCoursesLoading,
    isError: isPublicCoursesError,
  } = useGetPublicCoursesQuery();

  const publicCourses = publicCoursesResponse?.data ?? [];
  const selectedCourseTitles = selectedCourses
    .map((courseId) => publicCourses.find((course) => course._id === courseId))
    .filter(Boolean);

  const { data, isLoading, isError } = useGetInstructorProfileQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const instructorProfile = data?.data?.[0];

  const [updateInstructorInformation] =
    useUpdateInstructorInformationMutation();

  const [updateInstructorAcademicDetails] =
    useUpdateInstructorAcademicDetailsMutation();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      email: "",
      phoneNumber: "",
    },
  });
  useEffect(() => {
    if (instructorProfile) {
      reset({
        // Personal Info
        firstName: instructorProfile?.name?.split(" ")[0] || "",
        lastName: instructorProfile?.name?.split(" ").slice(1).join(" ") || "",
        fatherName: instructorProfile?.fatherName || "",
        email: instructorProfile?.email || "",
        phoneNumber: instructorProfile?.phoneNumber || "",
        dateOfBirth: instructorProfile?.dateOfBirth
          ? instructorProfile.dateOfBirth.split("T")[0]
          : "",
        bio: instructorProfile?.bio || "",

        // Residence Info
        address: instructorProfile?.residenceInfo?.address || "",
        city: instructorProfile?.residenceInfo?.city || "",
        country: instructorProfile?.residenceInfo?.country || "",
        postalCode: instructorProfile?.residenceInfo?.postalCode || "",

        // Emergency Info
        emergencyFullName: instructorProfile?.emergencyInfo?.fullName || "",
        emergencyRelationship:
          instructorProfile?.emergencyInfo?.relationship || "",
        emergencyPhoneNo:
          instructorProfile?.emergencyInfo?.phoneNumber || "",

        // Academic Info
        qualification: instructorProfile?.academicInfo?.qualification || "",
        degreeTitle: instructorProfile?.academicInfo?.degreeTitle || "",
        graduationYear: instructorProfile?.academicInfo?.graduationYear || "",
        totalMarks: instructorProfile?.academicInfo?.totalMarks || "",
        obtainedMarks: instructorProfile?.academicInfo?.obtainedMarks || "",
        institution: instructorProfile?.academicInfo?.institution || "",
      });

      // Profile Image
      setProfileImage(instructorProfile?.profilePicture || null);

      // Selected Courses
      setSelectedCourses(
        instructorProfile?.academicInfo?.coursePreferences || [],
      );

      setInitialSnapshot(snapshotFromInstructorProfile(instructorProfile));
    }
  }, [instructorProfile, reset]);
  useEffect(() => {
    return () => {
      if (
        typeof profileImage === "string" &&
        profileImage.startsWith("blob:")
      ) {
        URL.revokeObjectURL(profileImage);
      }
    };
  }, [profileImage]);

  const watchedValues = watch();
  const hasUnsavedChanges = useMemo(() => {
    if (!initialSnapshot) return false;

    if (profileImageFile) return true;

    const transcript = watchedValues.transcript;
    if (transcript instanceof File || transcript?.[0]) return true;

    const currentSnapshot = buildProfileSnapshot(
      watchedValues,
      selectedCourses,
    );
    return !snapshotsEqual(currentSnapshot, initialSnapshot);
  }, [
    initialSnapshot,
    watchedValues,
    selectedCourses,
    profileImageFile,
  ]);

  const onSubmit = async (formData) => {
    try {
      let coursesToSubmit = [...selectedCourses];
      if (
        currentCourse &&
        !coursesToSubmit.includes(currentCourse) &&
        coursesToSubmit.length < 5
      ) {
        coursesToSubmit = [...coursesToSubmit, currentCourse];
        setSelectedCourses(coursesToSubmit);
        setCurrentCourse("");
      }

      if (coursesToSubmit.length === 0) {
        setToast({
          show: true,
          type: "error",
          title: "Validation Error",
          message: "Please select at least one preferred course.",
        });
        return;
      }

      if (!profileImageFile && !instructorProfile?.profilePicture) {
        setProfileImageError("Profile picture is required");
        return;
      }

      setProfileImageError("");

      const transcriptFile =
        formData.transcript instanceof File
          ? formData.transcript
          : formData.transcript?.[0];

      const currentSnapshot = buildProfileSnapshot(formData, coursesToSubmit);
      const hasNewTranscript = Boolean(transcriptFile);
      const hasNewProfileImage = Boolean(profileImageFile);

      if (
        !hasNewProfileImage &&
        !hasNewTranscript &&
        initialSnapshot &&
        snapshotsEqual(currentSnapshot, initialSnapshot)
      ) {
        setToast({
          show: true,
          type: "error",
          title: "No changes",
          message: "Update a field before saving.",
        });
        return;
      }

      const submittedData = { ...formData };
      delete submittedData.coursePreferences;

      if (typeof submittedData.bio === "string") {
        submittedData.bio = submittedData.bio.trim();
      }

      delete submittedData.transcript;

      const infoChanged =
        hasNewProfileImage ||
        INFO_FIELD_KEYS.some(
          (key) =>
            normalizeValue(submittedData[key]) !==
            normalizeValue(initialSnapshot?.[key]),
        ) ||
        JSON.stringify([...coursesToSubmit].sort()) !==
          JSON.stringify(initialSnapshot?.courses || []);

      const academicChanged =
        hasNewTranscript ||
        ACADEMIC_FIELD_KEYS.some(
          (key) =>
            normalizeValue(submittedData[key]) !==
            normalizeValue(initialSnapshot?.[key]),
        );

      const infoFields = INFO_FIELD_KEYS;
      const academicFields = ACADEMIC_FIELD_KEYS;

      const infoFD = new FormData();
      infoFields.forEach((key) => {
        infoFD.append(key, submittedData[key] ?? "");
      });

      coursesToSubmit.forEach((course, index) => {
        infoFD.append(`coursePreferences[${index}]`, course);
      });

      if (profileImageFile) {
        infoFD.append("files", profileImageFile);
      }

      const academicFD = new FormData();
      academicFields.forEach((key) => {
        academicFD.append(key, submittedData[key] ?? "");
      });

      if (transcriptFile) {
        academicFD.append("files", transcriptFile);
      }

      if (infoChanged) {
        await updateInstructorInformation(infoFD).unwrap();
      }

      if (academicChanged) {
        await updateInstructorAcademicDetails(academicFD).unwrap();
      }

      setInitialSnapshot(currentSnapshot);
      setProfileImageFile(null);

      setToast({
        show: true,
        type: "success",
        title: "Success",
        message: "Profile updated successfully!",
      });

      setTimeout(() => {
        navigate("/instructor/profile", { replace: true });
      }, 1200);
    } catch (error) {
      setToast({
        show: true,
        type: "error",
        title: "Update Failed",
        message:
          error?.data?.message ||
          "Something went wrong while updating profile.",
      });
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setProfileImageError("Please choose an image file.");
      return;
    }

    const maxBytes = 1024 * 1024;
    if (file.size > maxBytes) {
      setProfileImageError("Image must be under 1MB.");
      return;
    }

    setProfileImageError("");
    const imageUrl = URL.createObjectURL(file);

    setProfileImage((previousImage) => {
      if (previousImage && previousImage.startsWith("blob:")) {
        URL.revokeObjectURL(previousImage);
      }
      return imageUrl;
    });
    setProfileImageFile(file);
  };

  useEffect(() => {
    setValue("coursePreferences", selectedCourses, {
      shouldValidate: true,
      shouldDirty: false,
    });
  }, [selectedCourses, setValue]);
  const navigate = useNavigate();
  const [toast, setToast] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
  });

  return (
    <InstructorLayout>
      <div className="mt-1 px-4 sm:px-6 lg:px-8">
        <div className="rounded-none border border-[#6055555E] bg-white px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
          <h1 className="mb-3 text-lg font-medium leading-[26px] text-black sm:text-[22px]">
            My Profile
          </h1>
          <div className="flex flex-col gap-4 rounded-none bg-[#089BC412] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-[26px] sm:py-3">
            <div className="flex min-w-0 items-center gap-4 sm:gap-6">
              <img
                src={profileImage || instructorProfile?.profilePicture}
                alt={loggedInUser?.firstName}
                className="h-12 w-12 shrink-0 rounded-full object-cover sm:h-14 sm:w-14"
              />

              <div className="min-w-0">
                <h2 className="truncate text-xl font-semibold leading-tight text-[#6B6B6B] sm:text-[26px] sm:leading-[26px]">
                  {instructorProfile?.name}
                </h2>

                <InstructorIdBadge
                  instructorId={
                    instructorProfile?.id || instructorProfile?._id
                  }
                />
              </div>
            </div>
            <button
              type="submit"
              form="profileForm"
              disabled={isSubmitting || !hasUnsavedChanges}
              className="w-full rounded-full bg-cyan-600 px-5 py-2 text-xs font-semibold text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:py-1.5"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 pb-10 sm:px-6 lg:px-8">
        <div className="mx-auto min-h-screen max-w-[934px]">
          <h1 className="mb-4 mt-5 inline-flex items-center gap-2 text-xl font-medium leading-7 text-black sm:mb-6 sm:mt-7 sm:gap-2.5 sm:text-2xl">
            <Edit /> <span>Edit Profile</span>
          </h1>

          <div>
            <form id="profileForm" noValidate onSubmit={handleSubmit(onSubmit)}>
              <div className={`${sectionClassName} pb-10 lg:pb-12 lg:pt-11`}>
                <h3 className={sectionTitleClassName}>
                  <OrangeProfileFilled color={"#089BC4"} /> Personal Information
                </h3>

                <label className="mb-8 mt-1.5 block cursor-pointer text-center sm:mb-14">
                  <div
                    className={`relative mx-auto mb-4 flex h-[100px] w-[100px] items-center justify-center rounded-full bg-[#089BC414] ${
                      profileImageError ? "ring-2 ring-[#C62828]" : ""
                    }`}
                  >
                    {profileImage || instructorProfile?.profilePicture ? (
                      <img
                        src={profileImage || instructorProfile?.profilePicture}
                        alt="Profile"
                        className="h-full w-full rounded-full object-cover"
                      />
                    ) : (
                      <OrangeProfile color="#089BC4" />
                    )}

                    <div className="absolute bottom-0 right-0">
                      <div className="bg-[#089BC4] flex items-center justify-center rounded-full w-[33px] h-[33px] ">
                        <Upload />
                      </div>
                    </div>
                  </div>

                  <p className="text-base font-light leading-5 text-black sm:text-xl">
                    Upload Profile Picture*
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfileImageChange}
                    className="hidden"
                  />
                </label>
                {profileImageError ? (
                  <p className="-mt-6 mb-6 text-center text-sm font-medium text-[#C62828] sm:-mt-10 sm:mb-10">
                    {profileImageError}
                  </p>
                ) : null}

                <div className={gridThreeClassName}>
                  <FormInput
                    id="firstName"
                    label="First Name*"
                    type="text"
                    placeholder="Enter First Name"
                    labelClassName={labelClassName}
                    inputClassName={inputClassName}
                    errorMessage={errors.firstName?.message}
                    {...register("firstName", {
                      required: "First name is required",
                    })}
                  />

                  <FormInput
                    id="lastName"
                    label="Last Name*"
                    type="text"
                    placeholder="Enter Last Name"
                    labelClassName={labelClassName}
                    inputClassName={inputClassName}
                    errorMessage={errors.lastName?.message}
                    {...register("lastName", {
                      required: "Last name is required",
                    })}
                  />

                  <FormInput
                    id="fatherName"
                    label="Father Name*"
                    type="text"
                    placeholder="Enter Father's Name"
                    labelClassName={labelClassName}
                    inputClassName={inputClassName}
                    errorMessage={errors.fatherName?.message}
                    {...register("fatherName", {
                      required: "Father's name is required",
                    })}
                  />
                </div>

                <div className={`${gridThreeClassName} mt-7`}>
                  <FormInput
                    id="email"
                    label="Email*"
                    type="email"
                    value={instructorProfile?.email}
                    labelClassName={labelClassName}
                    inputClassName={`${inputClassName} bg-[#C6C6C6B2]`}
                    readOnly
                  />

                  <div className="relative h-full w-full">
                    <label
                      htmlFor="countryCode"
                      className={`mb-1.5 block leading-5 ${labelClassName}`}
                    >
                      Phone No*
                    </label>
                    <Controller
                      name="phoneNumber"
                      control={control}
                      rules={{
                        required: "Phone number is required",
                        validate: {
                          internationalFormat: (value) =>
                            /^\+[1-9]\d{7,14}$/.test(value) ||
                            "Phone number must be in international format (e.g., +923001234567)",
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <PhoneInput
                          country={"pk"}
                          placeholder="Enter Phone no"
                          enableSearch
                          value={value || ""}
                          onChange={(phone) => {
                            const withPlus = phone.startsWith("+")
                              ? phone
                              : `+${phone}`;
                            onChange(withPlus);
                          }}
                          inputProps={{
                            id: "phoneNumber",
                            name: "phoneNumber",
                          }}
                          inputClass=" !h-[46px] !border !border-[#51CFF363] !rounded-lg !w-full !bg-transparent  !text-base  leading-6 !pl-14 text-black placeholder:text-[#8C8CA1] placeholder:font-light focus:!border-[#089BC4] focus:!outline-none"
                          buttonClass="!px-2 !rounded-l-lg "
                          dropdownClass="bg-white"
                        />
                      )}
                    />
                  </div>

                  <FormInput
                    id="dateOfBirth"
                    label="Date of Birth*"
                    type="date"
                    labelClassName={labelClassName}
                    inputClassName={inputClassName}
                    errorMessage={errors.dateOfBirth?.message}
                    {...register("dateOfBirth", {
                      required: "Date of birth is required",
                    })}
                  />
                </div>

                <div className="mt-7">
                  <label className={`${labelClassName} leading-5`}>
                    Bio*
                  </label>
                  <textarea
                    placeholder="Type here..."
                    maxLength={100}
                    className="px-5 mt-1.5 min-h-32 w-full rounded-lg border border-[#51CFF363] bg-transparent py-3  placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none resize-none"
                    {...register("bio", {
                      validate: (value) => {
                        const trimmed = (value ?? "").trim();
                        if (!trimmed) return "Bio is required";
                        if (trimmed.length < 20 || trimmed.length > 100) {
                          return "Bio must be 20–100 characters";
                        }
                        return true;
                      },
                      onBlur: (event) => {
                        const trimmed = event.target.value.trim();
                        if (trimmed !== event.target.value) {
                          setValue("bio", trimmed, { shouldValidate: true });
                        }
                      },
                    })}
                  />
                  {errors.bio && (
                    <p className="text-sm text-[#C62828]">
                      {errors.bio.message}
                    </p>
                  )}
                </div>
              </div>

              <div className={`${sectionClassName} pb-8 lg:pb-9`}>
                <h3 className={`mb-6 sm:mb-9 ${sectionTitleClassName}`}>
                  <ProfileLocationIcon color="#089BC4" /> Residence Information
                </h3>

                <FormInput
                  id="address"
                  label="Address*"
                  type="text"
                  placeholder="Street address"
                  labelClassName={labelClassName}
                  inputClassName={inputClassName}
                  errorMessage={errors.address?.message}
                  {...register("address", {
                    required: "Address is required",
                  })}
                />

                <div className={`${gridThreeClassName} mt-7`}>
                  <FormInput
                    id="city"
                    label="City*"
                    type="text"
                    placeholder="Enter City Name"
                    labelClassName={labelClassName}
                    inputClassName={inputClassName}
                    errorMessage={errors.city?.message}
                    {...register("city", { required: "City is required" })}
                  />

                  <div>
                    <label className={`${labelClassName} leading-5`}>
                      Country*
                    </label>
                    <select
                      className={`px-5 mt-1.5 h-[46px] w-full rounded-lg border border-[#51CFF363] bg-transparent   focus:border-[#089BC4] focus:outline-none appearance-none 
                            ${watch("country") ? "text-black" : "text-[#8C8CA1]"}`}
                      {...register("country", {
                        required: "Country is required",
                      })}
                    >
                      <option value="" disabled>
                        Select Country
                      </option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.country && (
                    <p className="text-sm text-[#C62828]">
                      {errors.country.message}
                    </p>
                  )}

                  <FormInput
                    id="postalCode"
                    label="Postal Code*"
                    type="text"
                    placeholder="12345"
                    labelClassName={labelClassName}
                    inputClassName={inputClassName}
                    errorMessage={errors.postalCode?.message}
                    {...register("postalCode", {
                      required: "Postal code is required",
                    })}
                  />
                </div>
              </div>

              <div className={`${sectionClassName} pb-8 lg:pb-9`}>
                <h3 className={`mb-6 sm:mb-9 ${sectionTitleClassName}`}>
                  <EmergencyContact color="#089BC4" /> Emergency Contacts
                </h3>

                <div className={gridThreeClassName}>
                  <FormInput
                    id="emergencyFullName"
                    label="Full Name*"
                    type="text"
                    placeholder="Enter Name"
                    labelClassName={labelClassName}
                    inputClassName={inputClassName}
                    errorMessage={errors.emergencyFullName?.message}
                    {...register("emergencyFullName", {
                      required: "Full name is required",
                    })}
                  />

                  <div>
                    <label className={`${labelClassName} leading-5`}>
                      Relationship*
                    </label>
                    <select
                      className={`px-5 mt-1.5 h-[46px]  w-full rounded-lg border border-[#51CFF363] bg-transparent  focus:border-[#089BC4] focus:outline-none appearance-none
                              
                              ${watch("emergencyRelationship") ? "text-black" : "text-[#8C8CA1]"}`}
                      {...register("emergencyRelationship", {
                        required: "Relationship is required",
                      })}
                      defaultValue={""}
                    >
                      <option value="" disabled hidden>
                        Select relationship
                      </option>
                      <option value="parent">Parent</option>
                      <option value="sibling">Sibling</option>
                      <option value="grandparent">Grandparent</option>
                      <option value="guardian">Guardian</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.emergencyRelationship && (
                      <p className="text-sm text-[#C62828]">
                        {errors.emergencyRelationship.message}
                      </p>
                    )}
                  </div>

                  <div className="relative h-full w-full md:col-span-2 lg:col-span-1">
                    <label
                      htmlFor="emergencyPhoneNo"
                      className={`mb-1.5 block leading-5 ${labelClassName}`}
                    >
                      Phone No*
                    </label>
                    <Controller
                      name="emergencyPhoneNo"
                      control={control}
                      rules={{
                        required: "Phone is required",
                        validate: {
                          internationalFormat: (value) =>
                            /^\+[1-9]\d{7,14}$/.test(value) ||
                            "Phone number must be in international format (e.g., +923001234567)",
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <PhoneInput
                          country={"pk"}
                          enableSearch
                          placeholder="+9231777383"
                          value={value || ""}
                          onChange={(phone) => {
                            const withPlus = phone.startsWith("+")
                              ? phone
                              : `+${phone}`;
                            onChange(withPlus);
                          }}
                          inputProps={{
                            id: "emergencyPhoneNo",
                            name: "emergencyPhoneNo",
                          }}
                          inputClass=" !h-[46px] !border !border-[#51CFF363] placeholder:font-light !rounded-lg !w-full !bg-transparent  !text-base  leading-6 !pl-14 text-black placeholder:text-[#8C8CA1] placeholder:font-light focus:!border-[#089BC4] focus:!outline-none"
                          buttonClass="!px-2 !rounded-l-lg "
                          dropdownClass="bg-white"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className={`${sectionClassName} pb-8 lg:pb-9`}>
                <h3 className={sectionTitleClassName}>
                  <GraduationCap color={"#089BC4"} /> Academic Details
                </h3>

                <div className={`${gridThreeClassName} mt-7`}>
                  <div>
                    <label className={`${labelClassName} leading-5`}>
                      Qualification*
                    </label>
                    <select
                      className={`px-5 mt-1.5 h-[46px]  w-full rounded-lg border border-[#51CFF363] bg-transparent  focus:border-[#089BC4] focus:outline-none appearance-none
                              
                              ${watch("qualification") ? "text-black" : "text-[#8C8CA1]"}`}
                      {...register("qualification", {
                        required: "qualification is required",
                      })}
                      defaultValue={"Select qualification"}
                    >
                      <option value="" disabled hidden>
                        Select qualification
                      </option>
                      <option value="Ph.D">Ph.D</option>
                      <option value="Master's Degree">Master's Degree</option>
                      <option value="Bachelor's Degree">Bachelor's Degree</option>
                    </select>
                    {errors.qualification && (
                      <p className="text-sm text-[#C62828]">
                        {errors.qualification.message}
                      </p>
                    )}
                  </div>

                  <FormInput
                    id="degreeTitle"
                    label="Degree Title*"
                    type="text"
                    placeholder="e.g., Computer Science"
                    labelClassName={labelClassName}
                    inputClassName={inputClassName}
                    errorMessage={errors.degreeTitle?.message}
                    {...register("degreeTitle", {
                      required: "Degree Title is required",
                    })}
                  />

                  <FormInput
                    id="graduationYear*"
                    label="Graduation Year*"
                    type="text"
                    labelClassName={labelClassName}
                    inputClassName={inputClassName}
                    errorMessage={errors.dateOfBirth?.message}
                    {...register("graduationYear", {
                      required: "Graduation Year is required",
                    })}
                  />
                </div>

                <div className={`${gridThreeClassName} mt-7`}>
                  <FormInput
                    id="totalMarks"
                    label="Total CGPA/Marks"
                    type="text"
                    placeholder="e.g., 4.00/ 1100"
                    labelClassName={labelClassName}
                    inputClassName={inputClassName}
                    errorMessage={errors.totalMarks?.message}
                    {...register("totalMarks", {
                      required: "Marks is required",
                    })}
                  />
                  <FormInput
                    id="obtainedMarks"
                    label="Obtained CGPA/Marks*"
                    type="text"
                    placeholder="e.g., 3.23/880"
                    labelClassName={labelClassName}
                    inputClassName={inputClassName}
                    errorMessage={errors.obtainedMarks?.message}
                    {...register("obtainedMarks", {
                      required: "Marks is required",
                    })}
                  />

                  <FormInput
                    id="institution"
                    label="Institution*"
                    type="text"
                    labelClassName={labelClassName}
                    inputClassName={inputClassName}
                    errorMessage={errors.institution?.message}
                    {...register("institution", {
                      required: "Institution is required",
                    })}
                  />
                </div>

                <div className="mt-7">
                  <label className={`${labelClassName} leading-5`}>
                    Upload Transcript*
                  </label>
                  <label className="mt-1.5 flex cursor-pointer items-center justify-center rounded-lg border border-[#51CFF363] px-4 py-8 transition-colors hover:border-[#089BC4] hover:bg-[#089BC414] sm:px-6 sm:pb-10 sm:pt-8">
                    <div className="text-center">
                      <div className="mx-auto mb-3 flex h-20 w-20 items-center justify-center rounded-full bg-[#089BC414] sm:h-[100px] sm:w-[100px]">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#089BC4] sm:h-12 sm:w-12">
                          <Upload />
                        </div>
                      </div>
                      <p className="text-base font-light leading-5 text-black sm:text-xl">
                        Click to upload transcript
                      </p>
                      <p className="mt-1.5 text-sm leading-5 text-black sm:text-lg">
                        PDF up to 10MB
                      </p>
                    </div>
                    <input
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setValue("transcript", file); // store in RHF without validation
                        }
                      }}
                    />
                  </label>
                </div>
              </div>

              <div className={`${sectionClassName} pb-5`}>
                <h3 className={`mb-4 sm:mb-6 ${sectionTitleClassName}`}>
                  <LockIcon color="#089BC4" /> Teaching Course Preferences
                </h3>

                <div>
                  <label className={`${labelClassName} mb-2 block leading-5`}>
                    Preferred Courses*
                  </label>
                  <div className="relative">
                    <select
                      className="px-5 h-[46px] bg-transparent w-full rounded-lg border border-[#51CFF363] focus:border-[#089BC4] focus:outline-none appearance-none font-light text-[#8C8CA1]"
                      value={currentCourse}
                      onChange={(e) => setCurrentCourse(e.target.value)}
                    >
                      <option value="" disabled hidden>
                        {isPublicCoursesLoading
                          ? "Loading courses..."
                          : "Select from drop down"}
                      </option>
                      {publicCourses.map((course) => {
                        const isSelected = selectedCourses.includes(course._id);

                        return (
                          <option
                            key={course._id}
                            value={course._id}
                            disabled={isSelected}
                            className={isSelected ? "text-gray-400" : ""}
                          >
                            {course.courseTitle}
                            {isSelected ? " (Selected)" : ""}
                          </option>
                        );
                      })}
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                      <FilledDropDown />
                    </div>
                  </div>

                  {errors.coursePreferences && (
                    <p className="mt-2 text-sm text-[#C62828]">
                      {errors.coursePreferences.message}
                    </p>
                  )}

                  {isPublicCoursesError ? (
                    <p className="mt-2 text-sm text-[#C62828]">
                      Unable to load courses right now.
                    </p>
                  ) : null}

                  {selectedCourseTitles.length > 0 && (
                    <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                      {selectedCourseTitles.map((course) => (
                        <div
                          key={course._id}
                          className="flex items-center justify-between gap-2 rounded-lg border border-[#51CFF363] bg-transparent pl-2 text-sm text-black sm:text-base"
                        >
                          <span className="min-w-0 flex-1 truncate py-2">
                            {course.courseTitle}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedCourses((prev) =>
                                prev.filter((c) => c !== course._id),
                              )
                            }
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#51CFF363] bg-[#E5E5E5] text-[#25252B] sm:h-12 sm:w-12"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          currentCourse &&
                          !selectedCourses.includes(currentCourse) &&
                          selectedCourses.length < 5
                        ) {
                          setSelectedCourses((prev) => [
                            ...prev,
                            currentCourse,
                          ]);
                          setCurrentCourse("");
                        }
                      }}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#51CFF363] bg-[#089BC4] px-5 py-2.5 text-base font-medium leading-5 text-white hover:bg-[#0785A0] sm:w-auto sm:text-xl"
                    >
                      <Plus />
                      Add Another
                    </button>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                btnText={isSubmitting ? "Saving..." : "Save Changes"}
                disabled={isSubmitting || !hasUnsavedChanges}
                btnClassName="mt-6 w-full rounded-lg bg-[#089BC4] px-6 py-2.5 text-base font-medium text-white transition-colors hover:bg-[#0789AD] disabled:cursor-not-allowed disabled:opacity-70 sm:text-xl"
              />
            </form>
          </div>
        </div>
      </div>
      {toast.show && (
        <Toast
          title={toast.title}
          message={toast.message}
          variant={toast.type}
          onClose={() => setToast((prev) => ({ ...prev, show: false }))}
        />
      )}
    </InstructorLayout>
  );
};

export default EditProfile;
